import React, { useState, useEffect } from 'react';
import { CONFIG } from '../config';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_code: '',
    variables: {},
    custom_groups: [],
  });
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [newGroup, setNewGroup] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [eventsResp, templatesResp] = await Promise.all([
        fetch(`${CONFIG.apiBaseUrl}/events`, { credentials: 'include' }),
        fetch(`${CONFIG.apiBaseUrl}/templates`, { credentials: 'include' }),
      ]);

      if (!eventsResp.ok) throw new Error('Failed to fetch events');
      const eventsData = await eventsResp.json();
      setEvents(eventsData.events || []);

      if (templatesResp.ok) {
        const tmplData = await templatesResp.json();
        setTemplates(tmplData.templates || []);
      }
    } catch (err) {
      console.error('fetchData failed:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (event) => {
    setFormData({
      event_name: event.event_name || '',
      event_code: event.event_code || '',
      variables: event.variables || {},
      custom_groups: event.custom_groups || ['Bride', 'Groom', 'Bridal Party', 'Event Staff'],
    });
    setEditingEvent(event);
    setSaveSuccess(false);
    setError(null);
    setNewVarKey('');
    setNewVarValue('');
    setNewGroup('');
    setPreviewTemplate(null);
  };

  const closeEdit = () => {
    setEditingEvent(null);
    setError(null);
    setSaveSuccess(false);
  };

  const addVariable = () => {
    if (!newVarKey.trim()) return;
    setFormData(prev => ({
      ...prev,
      variables: { ...prev.variables, [newVarKey.trim()]: newVarValue.trim() },
    }));
    setNewVarKey('');
    setNewVarValue('');
  };

  const removeVariable = (key) => {
    setFormData(prev => {
      const updated = { ...prev.variables };
      delete updated[key];
      return { ...prev, variables: updated };
    });
  };

  const addGroup = () => {
    const g = newGroup.trim();
    if (!g || formData.custom_groups.includes(g)) return;
    setFormData(prev => ({ ...prev, custom_groups: [...prev.custom_groups, g] }));
    setNewGroup('');
  };

  const removeGroup = (group) => {
    setFormData(prev => ({ ...prev, custom_groups: prev.custom_groups.filter(g => g !== group) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSaveSuccess(false);
    try {
      const resp = await fetch(
        `${CONFIG.apiBaseUrl}/events/${encodeURIComponent(editingEvent.event_id)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            variables: Object.keys(formData.variables).length > 0 ? formData.variables : undefined,
            custom_groups: formData.custom_groups,
          }),
        }
      );
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || data.message || 'Failed to update event');
      }
      setSaveSuccess(true);
      // Refresh list so the card reflects new name/code
      const eventsResp = await fetch(`${CONFIG.apiBaseUrl}/events`, { credentials: 'include' });
      if (eventsResp.ok) {
        const d = await eventsResp.json();
        setEvents(d.events || []);
        const updated = (d.events || []).find(ev => ev.event_id === editingEvent.event_id);
        if (updated) setEditingEvent(updated);
      }
    } catch (err) {
      console.error('Save failed:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getTemplateNames = (templateIds) => {
    if (!templateIds || templateIds.length === 0) return [];
    return templateIds.map(id => {
      const tmpl = templates.find(t => t.template_id === id);
      return tmpl ? { id, name: tmpl.template_name, channel: tmpl.channel } : { id, name: id, channel: null };
    });
  };

  const statusLabel = s => ({ pending: 'Provisioning AI\u2026', ready: 'AI Ready', error: 'Provisioning Failed' }[s] || '');

  if (loading) {
    return (
      <div className="whatsapp-responses-page">
        <h2 className="page-title">Events</h2>
        <div className="loading-message">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="whatsapp-responses-page">
      <h2 className="page-title">Events</h2>
      <p className="page-subtitle">View and update event details for your assigned events.</p>

      {error && !editingEvent && <div className="error-message">{error}</div>}

      {!editingEvent && (
        <div className="settings-section">
          {events.length === 0 ? (
            <div className="no-messages">No events found for your account.</div>
          ) : (
            <div className="event-list">
              {[...events].sort((a, b) => (a.event_name || '').localeCompare(b.event_name || '')).map(event => (
                <div key={event.event_id} className="event-card">
                  <div className="event-card-header">
                    <div>
                      <h4 className="event-card-title">{event.event_name}</h4>
                      <div className="event-card-id">{event.event_id}</div>
                    </div>
                    <div className="event-card-actions">
                      <button onClick={() => openEdit(event)} className="btn btn-secondary btn-sm">
                        Edit
                      </button>
                    </div>
                  </div>
                  <div className="event-card-details">
                    {event.event_code && (
                      <div className="event-detail">
                        <span className="detail-label">Event Code:</span>
                        <span className="detail-value">{event.event_code}</span>
                      </div>
                    )}
                    <div className="event-detail">
                      <span className="detail-label">AI:</span>
                      <span className="detail-value">
                        {event.lex_enabled ? 'Enabled' : 'Disabled'}
                        {event.lex_enabled && event.ai_provisioning_status && (
                          <span className={`status-badge ai-status-${event.ai_provisioning_status}`} style={{ marginLeft: '8px', fontSize: '0.8em' }}>
                            {statusLabel(event.ai_provisioning_status)}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="event-detail">
                      <span className="detail-label">#HeyDJ:</span>
                      <span className="detail-value">{event.dj_enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="event-detail">
                      <span className="detail-label">Media:</span>
                      <span className="detail-value">{event.media_enabled ? 'Enabled' : 'Disabled'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editingEvent && (
        <div className="settings-section">
          <h3 style={{ marginTop: 0 }}>Edit Event — {editingEvent.event_name}</h3>
          <div className="input-hint" style={{ marginBottom: '16px' }}>
            Event ID: <strong>{editingEvent.event_id}</strong>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group" style={{ opacity: 0.55 }}>
              <label className="form-label">Event Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_name}
                readOnly
                disabled
              />
            </div>

            <div className="form-group" style={{ opacity: 0.55 }}>
              <label className="form-label">Event Code (for public registration)</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_code}
                readOnly
                disabled
              />
              <div className="input-hint">Event name and code are managed by an administrator.</div>
            </div>

            <div className="form-group">
              <label className="form-label">Guest Groups</label>
              <div className="variables-list">
                <div className="variable-row">
                  <span className="variable-key" style={{ color: '#9ca3af' }}>All</span>
                  <span style={{ fontSize: '0.8em', color: '#9ca3af', fontStyle: 'italic' }}>always included</span>
                </div>
                {formData.custom_groups.map(group => (
                  <div key={group} className="variable-row">
                    <span className="variable-key">{group}</span>
                    <button type="button" onClick={() => removeGroup(group)} className="btn btn-danger btn-sm">
                      X
                    </button>
                  </div>
                ))}
              </div>
              <div className="variable-add-row">
                <input
                  type="text"
                  className="form-input form-input-sm"
                  value={newGroup}
                  onChange={(e) => setNewGroup(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGroup(); } }}
                  placeholder="New group (e.g., VIP, Speaker)"
                />
                <button type="button" onClick={addGroup} className="btn btn-secondary btn-sm" disabled={!newGroup.trim()}>
                  Add
                </button>
              </div>
              <div className="input-hint">"All" is always available. Add event-specific groups like Bride, Groom, VIP, etc.</div>
            </div>

            <div className="form-group">
              <label className="form-label">Variables</label>
              <div className="variables-list">
                {Object.entries(formData.variables).map(([key, value]) => (
                  <div key={key} className="variable-row">
                    <span className="variable-key">{key}</span>
                    <input
                      type="text"
                      className="form-input form-input-sm"
                      value={value}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        variables: { ...prev.variables, [key]: e.target.value },
                      }))}
                      placeholder="Value"
                    />
                    <button type="button" onClick={() => removeVariable(key)} className="btn btn-danger btn-sm">
                      X
                    </button>
                  </div>
                ))}
              </div>
              <div className="variable-add-row">
                <input
                  type="text"
                  className="form-input form-input-sm"
                  value={newVarKey}
                  onChange={(e) => setNewVarKey(e.target.value)}
                  placeholder="Key (e.g., bride_name)"
                />
                <input
                  type="text"
                  className="form-input form-input-sm"
                  value={newVarValue}
                  onChange={(e) => setNewVarValue(e.target.value)}
                  placeholder="Value (e.g., Sarah)"
                />
                <button type="button" onClick={addVariable} className="btn btn-secondary btn-sm" disabled={!newVarKey.trim()}>
                  Add
                </button>
              </div>
              <div className="input-hint">Custom variables used in message templates (e.g., bride_name, venue).</div>
            </div>

            {/* Assigned Templates — read-only */}
            <div className="form-group">
              <label className="form-label">Assigned Templates</label>
              {!editingEvent.template_ids || editingEvent.template_ids.length === 0 ? (
                <div className="input-hint">No templates assigned to this event.</div>
              ) : (
                <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
                  {getTemplateNames(editingEvent.template_ids).map(t => {
                    const full = templates.find(tmpl => tmpl.template_id === t.id);
                    return (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setPreviewTemplate(previewTemplate?.template_id === t.id ? null : full || t)}
                        style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          background: previewTemplate?.template_id === t.id ? '#e9d8ff' : '#f5f0ff',
                          border: `1px solid ${previewTemplate?.template_id === t.id ? '#a87fd4' : '#ddd'}`,
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          color: '#555',
                          cursor: 'pointer',
                        }}
                      >
                        {t.name}
                        {t.channel && <span style={{ color: '#999', marginLeft: '4px' }}>({t.channel})</span>}
                      </button>
                    );
                  })}
                </div>

                {previewTemplate && (
                  <div style={{
                    marginTop: '8px',
                    padding: '12px 16px',
                    background: '#faf8ff',
                    border: '1px solid #d8c8f0',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <strong style={{ color: '#333' }}>{previewTemplate.template_name || previewTemplate.name}</strong>
                      <button
                        type="button"
                        onClick={() => setPreviewTemplate(null)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999', fontSize: '1rem', lineHeight: 1 }}
                      >
                        ✕
                      </button>
                    </div>
                    {previewTemplate.channel && (
                      <div style={{ color: '#666', marginBottom: '8px', fontSize: '0.8rem' }}>
                        Channel: <strong>{previewTemplate.channel}</strong>
                        {previewTemplate.event_type && <> &nbsp;·&nbsp; Type: <strong>{previewTemplate.event_type}</strong></>}
                      </div>
                    )}
                    {previewTemplate.message_body ? (
                      <pre style={{
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontFamily: 'inherit',
                        color: '#333',
                        background: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        border: '1px solid #eee',
                        fontSize: '0.875rem',
                      }}>
                        {previewTemplate.message_body}
                      </pre>
                    ) : (
                      <div style={{ color: '#999', fontStyle: 'italic' }}>No message body available.</div>
                    )}
                    {previewTemplate.variables && previewTemplate.variables.length > 0 && (
                      <div style={{ marginTop: '8px', color: '#666', fontSize: '0.8rem' }}>
                        Variables: {previewTemplate.variables.map(v => (
                          <code key={v} style={{ background: '#eee', padding: '1px 5px', borderRadius: '3px', marginRight: '4px' }}>
                            {`{{${v}}}`}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                </>
              )}
              <div className="input-hint">Template assignments are managed by an administrator. Click a template to preview it.</div>
            </div>

            {/* Feature Flags — read-only */}
            <div className="form-group">
              <label className="form-label">Features</label>
              <div style={{ opacity: 0.55, pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label className="template-checkbox-label">
                  <input type="checkbox" readOnly checked={!!editingEvent.lex_enabled} />
                  <span className="template-checkbox-name">AI</span>
                  {editingEvent.lex_enabled && editingEvent.ai_provisioning_status && (
                    <span className={`status-badge ai-status-${editingEvent.ai_provisioning_status}`} style={{ marginLeft: '8px' }}>
                      {statusLabel(editingEvent.ai_provisioning_status)}
                    </span>
                  )}
                </label>
                <label className="template-checkbox-label">
                  <input type="checkbox" readOnly checked={!!editingEvent.dj_enabled} />
                  <span className="template-checkbox-name">#HeyDJ Song Requests</span>
                </label>
                <label className="template-checkbox-label">
                  <input type="checkbox" readOnly checked={!!editingEvent.media_enabled} />
                  <span className="template-checkbox-name">Media Collection</span>
                </label>
              </div>
              <div className="input-hint">Feature settings are managed by an administrator.</div>
            </div>

            {saveSuccess && (
              <div className="success-message" style={{ marginBottom: '16px' }}>
                Event updated successfully.
              </div>
            )}
            {error && (
              <div className="error-message" style={{ marginBottom: '16px' }}>
                {error}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={closeEdit} className="btn btn-secondary" disabled={saving}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EventsPage;
